/**
 * PositionDetailDrawer — slide-in panel for one position.
 *
 * Fetches `/positions/{id}` lazily (only when the drawer is open).
 * Renders three sections:
 *
 *   1. Header KPIs — entry, exit, PnL, duration
 *   2. Risk levels — TP/SL/leverage/margin
 *   3. Stats     — peaks/MFE/MAE/fees
 *
 * Visual rule of thumb: the drawer is read-only and dense. No edits,
 * no charts — those would live in a follow-up.
 */
import { useTranslation } from 'react-i18next'
import { Drawer } from '@/components/ui/Drawer'
import { usePositionDetail } from '@/hooks/useDashboardApi'
import { cn } from '@/lib/cn'
import {
  formatNumber, formatPct, formatUsd, timeAgo,
} from '@/lib/format'

interface PositionDetailDrawerProps {
  positionId: number | null
  onClose: () => void
}

export function PositionDetailDrawer({
  positionId, onClose,
}: PositionDetailDrawerProps) {
  const { t } = useTranslation('app')
  const { data, isLoading, error } = usePositionDetail(positionId)

  const title = data
    ? `${data.symbol} · ${t(`dashboard.open_positions.side.${data.direction}`)}`
    : t('position_detail.title')

  const subtitle = data
    ? `#${data.id} · ${data.is_open
        ? t('position_detail.open')
        : t('position_detail.closed_ago', { ago: timeAgo(data.closed_at ?? data.opened_at) })}`
    : undefined

  return (
    <Drawer
      open={positionId != null}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
    >
      {isLoading || !data ? (
        <div className="px-5 py-10 text-center text-textFaint text-sm">
          {error ? t('position_detail.error') : '—'}
        </div>
      ) : (
        <div className="px-5 py-4 space-y-6 font-mono">
          {/* Top KPIs */}
          <div className="grid grid-cols-2 gap-3">
            <KpiBox
              label={t('position_detail.entry')}
              value={formatNumber(data.entry_price, 4)}
            />
            <KpiBox
              label={data.is_open
                ? t('position_detail.mark')
                : t('position_detail.exit')}
              value={
                data.is_open
                  ? data.current_price != null
                    ? formatNumber(data.current_price, 4)
                    : '—'
                  : data.exit_price != null
                  ? formatNumber(data.exit_price, 4)
                  : '—'
              }
            />
            <KpiBox
              label={t('position_detail.pnl')}
              value={pnlText(data)}
              tone={pnlTone(data)}
            />
            <KpiBox
              label={t('position_detail.duration')}
              value={
                data.duration_minutes != null
                  ? formatDuration(data.duration_minutes)
                  : data.is_open
                  ? timeAgo(data.opened_at)
                  : '—'
              }
            />
          </div>

          {/* Risk levels */}
          <Section title={t('position_detail.risk')}>
            <Row
              label={t('position_detail.size')}
              value={`${formatNumber(data.quantity, 4)} · ×${data.leverage}`}
            />
            <Row
              label={t('position_detail.margin')}
              value={data.margin_used != null ? formatUsd(data.margin_used) : '—'}
            />
            <Row
              label={t('position_detail.tp')}
              value={data.take_profit_price != null
                ? formatNumber(data.take_profit_price, 4)
                : '—'}
            />
            <Row
              label={t('position_detail.sl')}
              value={data.stop_loss_price != null
                ? formatNumber(data.stop_loss_price, 4)
                : '—'}
            />
            {!data.is_open && (
              <Row
                label={t('position_detail.exit_reason')}
                value={
                  data.exit_reason
                    ? t(`exit_reason.${data.exit_reason}`, {
                        defaultValue: data.exit_reason,
                      })
                    : '—'
                }
              />
            )}
            {data.dca_level > 0 && (
              <Row
                label={t('position_detail.dca_level')}
                value={`DCA${data.dca_level}`}
                valueClass="text-fire"
              />
            )}
          </Section>

          {/* Stats */}
          <Section title={t('position_detail.stats')}>
            <Row
              label={t('position_detail.peak')}
              value={
                data.peak_pnl_pct != null
                  ? `${formatPct(data.peak_pnl_pct, { sign: true })}${
                      data.peak_price != null
                        ? ` @ ${formatNumber(data.peak_price, 4)}`
                        : ''
                    }`
                  : '—'
              }
              valueClass="text-pulse"
            />
            <Row
              label={t('position_detail.trough')}
              value={
                data.trough_pnl_pct != null
                  ? `${formatPct(data.trough_pnl_pct, { sign: true })}${
                      data.trough_price != null
                        ? ` @ ${formatNumber(data.trough_price, 4)}`
                        : ''
                    }`
                  : '—'
              }
              valueClass="text-rose"
            />
            <Row
              label="MFE / MAE"
              value={
                data.max_favorable_pct != null || data.max_adverse_pct != null
                  ? `${formatPct(data.max_favorable_pct ?? 0, { sign: true })} / ${
                      formatPct(data.max_adverse_pct ?? 0, { sign: true })
                    }`
                  : '—'
              }
            />
            <Row
              label={t('position_detail.fees')}
              value={
                feesText(data.entry_fee, data.exit_fee, data.funding_paid) ?? '—'
              }
            />
            <Row
              label={t('position_detail.opened_at')}
              value={new Date(data.opened_at).toLocaleString()}
            />
            {data.closed_at && (
              <Row
                label={t('position_detail.closed_at')}
                value={new Date(data.closed_at).toLocaleString()}
              />
            )}
          </Section>
        </div>
      )}
    </Drawer>
  )
}

/* ─── helpers ─────────────────────────────────────────────────────── */

function pnlText(p: {
  is_open: boolean
  unrealized_pnl: number | null
  unrealized_pnl_pct: number | null
  realized_pnl: number | null
  realized_pnl_pct: number | null
}): string {
  const pnl = p.is_open ? p.unrealized_pnl : p.realized_pnl
  const pct = p.is_open ? p.unrealized_pnl_pct : p.realized_pnl_pct
  if (pnl == null && pct == null) return '—'
  const parts: string[] = []
  if (pnl != null) parts.push(formatUsd(pnl, { sign: true }))
  if (pct != null) parts.push(formatPct(pct, { sign: true }))
  return parts.join(' · ')
}

function pnlTone(p: {
  is_open: boolean
  unrealized_pnl: number | null
  realized_pnl: number | null
}): 'pulse' | 'rose' | undefined {
  const pnl = p.is_open ? p.unrealized_pnl : p.realized_pnl
  if (pnl == null) return undefined
  return pnl >= 0 ? 'pulse' : 'rose'
}

function feesText(
  entry: number | null, exit: number | null, funding: number | null,
): string | null {
  const total = (entry ?? 0) + (exit ?? 0) + (funding ?? 0)
  if (entry == null && exit == null && funding == null) return null
  return formatUsd(total)
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h < 24) return m ? `${h}h ${m}m` : `${h}h`
  const d = Math.floor(h / 24)
  return `${d}d ${h % 24}h`
}

/* ─── building blocks ─────────────────────────────────────────────── */

function KpiBox({
  label, value, tone,
}: {
  label: string
  value: string
  tone?: 'pulse' | 'rose'
}) {
  return (
    <div className="bg-ink p-3 border border-line">
      <div className="text-textFaint text-2xs uppercase tracking-wider">
        {label}
      </div>
      <div className={cn(
        'mt-1 text-sm tabular-nums',
        tone === 'pulse' ? 'text-pulse' : tone === 'rose' ? 'text-rose' : 'text-text',
      )}>
        {value}
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-textFaint text-xs uppercase tracking-wider mb-2">
        {title}
      </h3>
      <div className="divide-y divide-line/40 border border-line bg-ink">
        {children}
      </div>
    </section>
  )
}

function Row({
  label, value, valueClass,
}: {
  label: string
  value: string
  valueClass?: string
}) {
  return (
    <div className="flex items-center px-3 py-2 text-xs">
      <span className="text-textDim">{label}</span>
      <span className={cn('ml-auto tabular-nums', valueClass ?? 'text-text')}>
        {value}
      </span>
    </div>
  )
}
